import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useSearchFiltersStore } from '@/stores/searchFiltersStore'
import { slugify } from '@/utils/strings'
import type { SuggestionClient } from '@/services/listingDataService/types/suggestion'
import type { FormSearchFiltersComponentModule } from '@/components/BrandedV2/FormSearchFilters/types'
import { LOCATION_TYPE_LIST } from '@/services/listingDataService/enums'

const useRealEstateSearch = (currentPath: string, emit: (...args: any) => void) => {
  const searchFiltersStore = useSearchFiltersStore()
  const { currentFilters, selectedFilters } = storeToRefs(searchFiltersStore)

  const searchTerm = ref('')

  const buildLocation = (data: any) => {
    const { type } = data

    const typeMap: any = {
      city: ['city'],
      zone: ['city', 'zone'],
      neighborhood: ['city', 'zone', 'neighborhood']
    }

    const location: any = { type }

    typeMap[type]?.forEach((key: any) => {
      if (data[key]) location[key] = slugify(data[key])
    })

    location.businessHub = slugify(data.businessHub)

    return location
  }

  const onSubmit = async () => {
    const businessHubs = selectedFilters.value.businessHubs
    const neighborhoods = selectedFilters.value.neighborhoods
    const locations = selectedFilters.value.locations
    if (searchTerm.value) {
      await searchFiltersStore.updateFilters({ ...currentFilters.value, businessHubs, neighborhoods, locations } as any, currentPath)
    }
  }

  const updateLocationFilter = (suggestion: SuggestionClient.Suggestion | null) => {
    if (suggestion) {
      const location = buildLocation(suggestion)
      selectedFilters.value.businessHub = slugify(suggestion.businessHub)
      const locations = [...selectedFilters.value.locations, location]
      selectedFilters.value.locations = [...new Set(locations.map(location => JSON.stringify(location)))].map(str => JSON.parse(str))
    }
  }

  const suggestionSelected = (suggestion: SuggestionClient.Suggestion | null) => {
    if (!suggestion) {
      updateLocationFilter(suggestion)
      return
    }

    if (suggestion.type === LOCATION_TYPE_LIST.RESIDENTIAL_COMPLEX && suggestion.residentialComplex) {
      emit('residential-complex-selected', {
        path: suggestion.residentialComplex.path,
        residentialComplex: suggestion.residentialComplex
      })

      return
    }

    updateLocationFilter(suggestion)
  }

  const setNeighborhoodsBySuggestion = (suggestion: SuggestionClient.Suggestion) => {
    const type = suggestion.type as LOCATION_TYPE_LIST.NEIGHBORHOOD | LOCATION_TYPE_LIST.ZONE | LOCATION_TYPE_LIST.CITY
    searchFiltersStore.setNeighborhoodsByType(suggestion.type, suggestion.businessHub, suggestion[type])
  }

  const filtersChange = async (filters: FormSearchFiltersComponentModule.Filters) => {
    await searchFiltersStore.updateFilters(filters, currentPath)
  }

  return {
    searchTerm,
    selectedFilters,
    buildLocation,
    filtersChange,
    onSubmit,
    updateLocationFilter,
    suggestionSelected,
    setNeighborhoodsBySuggestion
  }
}

export default useRealEstateSearch
